import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { ROLE_TYPE_BUSINESS_VENDOR } from "../../../constants/RegisterConstants";
import { BUSINESS_VENDOR_TARGET_ROLE_OPTIONS, BUSINESS_VENDOR_TYPES, getErrorTitle } from "../../../constants/constants";
import {
  dashboardPageContainerStyle,
  dashboardSectionCardStyle,
  renderDashboardHeader,
} from "./dashboardStyles";
import {
  appAccentPanelStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
} from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const DEFAULT_VENDOR_PROFILE = {
  businessName: "",
  businessType: "",
  serviceCategories: [] as string[],
  servesRoles: [] as string[],
  contactName: "",
  email: "",
  phone: "",
  website: "",
  formattedAddress: "",
  serviceRadiusMiles: "",
  description: "",
  licenseInfo: "",
  insuranceInfo: "",
  active: true,
};

const DashboardVendor = () => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const userRoleType = authStore.getUserRoleType();
  const mobile = useResponsive().isMobile;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<any>(DEFAULT_VENDOR_PROFILE);
  const [profileExists, setProfileExists] = useState(false);
  const [sharedProvidersLoading, setSharedProvidersLoading] = useState(true);
  const [sharedProviders, setSharedProviders] = useState<any[]>([]);
  const [expandedRows, setExpandedRows] = useState<any>(null);

  useEffect(() => {
    const fetchVendorProfile = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getVendorProfile();
        if (response) {
          setVendorProfile({
            ...DEFAULT_VENDOR_PROFILE,
            ...response,
            serviceCategories: Array.isArray(response?.serviceCategories) ? response.serviceCategories : [],
            servesRoles: Array.isArray(response?.servesRoles) ? response.servesRoles : [],
            serviceRadiusMiles: response?.serviceRadiusMiles ?? "",
          });
          setProfileExists(true);
        }
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
      } finally {
        setLoading(false);
      }
    };

    fetchVendorProfile();
  }, [backendAPIStore]);

  useEffect(() => {
    const fetchSharedProviders = async () => {
      setSharedProvidersLoading(true);
      try {
        await backendAPIStore.init();
        const [actorLinks, residentRoleLinks] = await Promise.all([
          backendAPIStore.getActorLinks("approved", "provider"),
          backendAPIStore.getResidentRoleLinks({ status: "approved", roleType: ROLE_TYPE_BUSINESS_VENDOR }),
        ]);

        const providerMap = new Map<string, any>();

        (actorLinks || []).forEach((actorLink: any) => {
          const isIncoming = actorLink?.direction === "incoming";
          const providerId = String(isIncoming ? actorLink?.requesterActorId || "" : actorLink?.targetActorId || "");
          const providerName = isIncoming
            ? actorLink?.metadata?.requesterDisplayName || "Shared Provider"
            : actorLink?.metadata?.targetDisplayName || "Shared Provider";
          const email = isIncoming
            ? actorLink?.metadata?.requesterEmail || ""
            : actorLink?.metadata?.targetEmail || "";
          const actorLinkId = String(actorLink?.linkId || "");

          providerMap.set(actorLinkId || providerId || providerName, {
            providerId: actorLinkId || providerId || providerName,
            providerName,
            email,
            residentCount: 0,
            residents: [],
          });
        });

        (residentRoleLinks || []).forEach((residentLink: any) => {
          const providerKey = String(residentLink?.actorLinkId || "");
          const provider = providerMap.get(providerKey);
          if (!provider) {
            return;
          }

          provider.residents.push({
            residentId: residentLink?.roleResidentId || residentLink?.providerResidentId || residentLink?.linkId,
            providerResidentId: residentLink?.providerResidentId || "",
            residentName: residentLink?.residentName || "Resident",
            sharedAccess: !!residentLink?.roleResidentId,
          });
          provider.residentCount = provider.residents.length;
        });

        setSharedProviders(
          Array.from(providerMap.values()).sort((first: any, second: any) =>
            String(first?.providerName || "").localeCompare(String(second?.providerName || "")),
          ),
        );
      } catch (error: any) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
      } finally {
        setSharedProvidersLoading(false);
      }
    };

    fetchSharedProviders();
  }, [backendAPIStore]);

  const handleFieldChange = (field: string, value: any) => {
    setVendorProfile((current: any) => ({
      ...current,
      [field]: value,
    }));
  };

  const selectedServiceNames = useMemo(() => {
    return (vendorProfile.serviceCategories || []).filter(Boolean);
  }, [vendorProfile.serviceCategories]);

  const selectedRoleNames = useMemo(() => {
    const roleLabelMap = new Map(BUSINESS_VENDOR_TARGET_ROLE_OPTIONS.map((option) => [option.code, option.name]));
    return (vendorProfile.servesRoles || []).map((roleCode: string) => roleLabelMap.get(roleCode) || roleCode);
  }, [vendorProfile.servesRoles]);

  const sourceTagStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
    padding: "0.15rem 0.55rem",
    fontSize: "0.74rem",
    fontWeight: 700,
    border: "1px solid #bae6fd",
    background: "#f0f9ff",
    color: "#0c4a6e",
  } as const;

  const residentsCountBody = (provider: any) => {
    const count = provider.residentCount ?? provider.residents?.length ?? 0;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: "1.6rem", height: "1.6rem", borderRadius: "999px",
        background: count > 0 ? "#eff6ff" : "#f1f5f9",
        border: count > 0 ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
        color: count > 0 ? "#185DA2" : "#94a3b8",
        fontWeight: 700, fontSize: "0.82rem", padding: "0 0.4rem",
      }}>
        {count}
      </span>
    );
  };

  const providerNameBody = (provider: any) => (
    <div>
      <div className="flex align-items-center gap-2 flex-wrap">
        <span style={{ fontWeight: 600 }}>{provider.providerName}</span>
        <span style={sourceTagStyle}>Shared</span>
      </div>
      {provider.email ? (
        <div style={{ color: "#64748b", fontSize: "0.85rem", marginTop: "0.2rem" }}>{provider.email}</div>
      ) : null}
    </div>
  );

  const rowExpansionTemplate = (provider: any) => {
    const residents = provider.residents || [];
    if (residents.length === 0) {
      return (
        <div className="p-3" style={{ color: "#64748b", fontStyle: "italic" }}>
          No residents have been shared with you under this provider yet.
        </div>
      );
    }
    return (
      <DataTable value={residents} dataKey="residentId" size="small" style={{ marginLeft: "3rem" }}>
        <Column
          header="Name"
          body={(r: any) => (
            <div className="flex align-items-center gap-2 flex-wrap">
              <span style={{ fontWeight: 600 }}>
                {r.residentName || "Resident"}
              </span>
              <span style={sourceTagStyle}>Shared</span>
              <span
                style={{
                  ...sourceTagStyle,
                  border: r?.sharedAccess ? "1px solid #bbf7d0" : "1px solid #fde68a",
                  background: r?.sharedAccess ? "#f0fdf4" : "#fffbeb",
                  color: r?.sharedAccess ? "#166534" : "#92400e",
                }}
              >
                {r?.sharedAccess ? "Ready" : "Pending"}
              </span>
            </div>
          )}
          style={{ fontWeight: 600 }}
        />
      </DataTable>
    );
  };

  const saveVendorProfile = async () => {
    setSaving(true);
    try {
      await backendAPIStore.init();
      const requestBody = {
        ...vendorProfile,
        serviceRadiusMiles: vendorProfile.serviceRadiusMiles === "" ? "" : Number(vendorProfile.serviceRadiusMiles),
      };

      const response = profileExists
        ? await backendAPIStore.updateVendorProfile(requestBody)
        : await backendAPIStore.addVendorProfile(requestBody);

      setVendorProfile({
        ...DEFAULT_VENDOR_PROFILE,
        ...response,
        serviceCategories: Array.isArray(response?.serviceCategories) ? response.serviceCategories : [],
        servesRoles: Array.isArray(response?.servesRoles) ? response.servesRoles : [],
        serviceRadiusMiles: response?.serviceRadiusMiles ?? "",
      });
      setProfileExists(true);
      toast.current?.show({
        severity: "success",
        summary: "Vendor profile saved",
        detail: "Your business details are ready for future connection and directory workflows.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error["message"], life: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (userRoleType && userRoleType !== ROLE_TYPE_BUSINESS_VENDOR) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div style={dashboardPageContainerStyle}>
        {renderDashboardHeader(
          "Business Vendor Dashboard",
          `Build your ${authExperience.brandName} business profile, choose the services you offer, and define whether you want to work with providers, nurses, or caregivers.`,
          <Button label={profileExists ? "Update Business Profile" : "Save Business Profile"} severity="warning" rounded onClick={saveVendorProfile} loading={saving} />
        )}

        {loading ? (
          <div className="col-12 text-center d-flex justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (
          <div className="grid">
            <div className="col-12">
              <Card style={dashboardSectionCardStyle}>
                <div style={{ ...appAccentPanelStyle, marginBottom: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>Shared provider and resident access</div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    Approved provider connections and any residents shared with your business appear here in the same care-network view used across {authExperience.brandName}.
                  </div>
                </div>

                {sharedProvidersLoading ? (
                  <div className="card flex justify-content-center p-5">
                    <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                  </div>
                ) : (
                  <div style={{ overflowX: "auto", width: "100%" }}>
                    <DataTable
                      value={sharedProviders}
                      dataKey="providerId"
                      expandedRows={expandedRows}
                      onRowToggle={(e) => setExpandedRows(e.data)}
                      rowExpansionTemplate={rowExpansionTemplate}
                      emptyMessage="No shared providers yet."
                      stripedRows
                      size="small"
                      scrollable={!mobile}
                      style={{ borderRadius: "10px", overflow: "hidden", minWidth: mobile ? "600px" : undefined }}
                    >
                      <Column expander style={{ width: "3rem" }} />
                      <Column header="Provider Name" body={providerNameBody} style={{ minWidth: "12rem" }} />
                      {!mobile && <Column field="email" header="Email" style={{ minWidth: "12rem" }} />}
                      <Column header="Residents" body={residentsCountBody} style={{ width: "6rem", textAlign: "center" }} />
                    </DataTable>
                  </div>
                )}
              </Card>
            </div>
            <div className="col-12 lg:col-8">
              <Card style={dashboardSectionCardStyle}>
                <div style={{ ...appAccentPanelStyle, marginBottom: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>In-scope business categories</div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    Start with your primary business type, then add other service categories you want providers, nurses, and caregivers to discover later through Connections.
                  </div>
                </div>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorBusinessName" className="font-medium block mb-2">Business Name</label>
                    <InputText id="vendorBusinessName" value={vendorProfile.businessName} onChange={(e) => handleFieldChange("businessName", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorContactName" className="font-medium block mb-2">Primary Contact</label>
                    <InputText id="vendorContactName" value={vendorProfile.contactName} onChange={(e) => handleFieldChange("contactName", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorEmail" className="font-medium block mb-2">Email</label>
                    <InputText id="vendorEmail" value={vendorProfile.email} onChange={(e) => handleFieldChange("email", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorPhone" className="font-medium block mb-2">Phone</label>
                    <InputText id="vendorPhone" value={vendorProfile.phone} onChange={(e) => handleFieldChange("phone", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorWebsite" className="font-medium block mb-2">Website</label>
                    <InputText id="vendorWebsite" value={vendorProfile.website} onChange={(e) => handleFieldChange("website", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorRadius" className="font-medium block mb-2">Service Radius (Miles)</label>
                    <InputText id="vendorRadius" value={vendorProfile.serviceRadiusMiles} onChange={(e) => handleFieldChange("serviceRadiusMiles", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12">
                    <label htmlFor="vendorBusinessType" className="font-medium block mb-2">Primary Business Type</label>
                    <Dropdown
                      id="vendorBusinessType"
                      value={vendorProfile.businessType}
                      options={BUSINESS_VENDOR_TYPES}
                      optionLabel="name"
                      optionValue="code"
                      onChange={(e) => handleFieldChange("businessType", e.value)}
                      placeholder="Select your primary business type"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="vendorServices" className="font-medium block mb-2">Additional Service Categories</label>
                    <MultiSelect
                      id="vendorServices"
                      value={vendorProfile.serviceCategories}
                      options={BUSINESS_VENDOR_TYPES}
                      optionLabel="name"
                      optionValue="code"
                      onChange={(e) => handleFieldChange("serviceCategories", e.value)}
                      placeholder="Choose all services that fit your business"
                      display="chip"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="vendorRoles" className="font-medium block mb-2">Who Do You Want To Work With?</label>
                    <MultiSelect
                      id="vendorRoles"
                      value={vendorProfile.servesRoles}
                      options={BUSINESS_VENDOR_TARGET_ROLE_OPTIONS}
                      optionLabel="name"
                      optionValue="code"
                      onChange={(e) => handleFieldChange("servesRoles", e.value)}
                      placeholder={`Select the ${authExperience.brandName} roles you want to work with`}
                      display="chip"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="vendorAddress" className="font-medium block mb-2">Business Address / Service Area</label>
                    <InputText id="vendorAddress" value={vendorProfile.formattedAddress} onChange={(e) => handleFieldChange("formattedAddress", e.target.value)} className="w-full" />
                  </div>
                  <div className="col-12">
                    <label htmlFor="vendorDescription" className="font-medium block mb-2">Business Description</label>
                    <InputTextarea id="vendorDescription" value={vendorProfile.description} onChange={(e) => handleFieldChange("description", e.target.value)} rows={5} className="w-full" autoResize />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorLicense" className="font-medium block mb-2">Licensing / Certifications</label>
                    <InputTextarea id="vendorLicense" value={vendorProfile.licenseInfo} onChange={(e) => handleFieldChange("licenseInfo", e.target.value)} rows={4} className="w-full" autoResize />
                  </div>
                  <div className="col-12 md:col-6">
                    <label htmlFor="vendorInsurance" className="font-medium block mb-2">Insurance / Compliance Notes</label>
                    <InputTextarea id="vendorInsurance" value={vendorProfile.insuranceInfo} onChange={(e) => handleFieldChange("insuranceInfo", e.target.value)} rows={4} className="w-full" autoResize />
                  </div>
                </div>
              </Card>
            </div>

            <div className="col-12 lg:col-4">
              <Card style={dashboardSectionCardStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Business Snapshot</div>
                <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.8rem", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.04em" }}>Primary Type</div>
                  <div className="mt-2" style={{ fontWeight: 700, color: "#0f172a" }}>{vendorProfile.businessType || "Not selected yet"}</div>
                </div>
                <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.8rem", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.04em" }}>Service Categories</div>
                  <div className="mt-2" style={{ color: "#0f172a", lineHeight: 1.7 }}>
                    {selectedServiceNames.length > 0 ? selectedServiceNames.join(", ") : "Choose the business categories you want in scope."}
                  </div>
                </div>
                <div style={{ ...appNeutralPanelStyle, marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.8rem", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.04em" }}>Works With</div>
                  <div className="mt-2" style={{ color: "#0f172a", lineHeight: 1.7 }}>
                    {selectedRoleNames.length > 0 ? selectedRoleNames.join(", ") : "Pick whether you want to work with providers, nurses, or caregivers."}
                  </div>
                </div>
                <div style={appAccentPanelStyle}>
                  <div style={{ fontWeight: 700, color: "#9a3412" }}>Next step</div>
                  <div className="mt-2" style={appMutedTextStyle}>
                    Once your vendor profile is saved, we can connect this dashboard into Connections so providers, nurses, and caregivers can discover and connect with your business.
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardVendor;
